// src/components/support/forms/ExtendedDaycare.js
import React, { useState } from "react";
import { ChevronLeft } from "lucide-react";
import { useRequestProcessor } from "../../../requestProcessor";
import SpinnerLoading from "../../common/SpinnerLoading";
import FootprintDatePicker from "../../common/FootprintDatePicker";
import moment from "moment";

import { useAtom } from "jotai";
import { childList } from "../../../atomstore/store";
import { useNavigate } from "react-router-dom";

import { submitExtendedDaycare } from "../../../queryfunctions/queryfunctions";
import CustomSelect from "../../common/CustomSelect";

const ExtendedDaycare = () => {
    const navigate = useNavigate();
    const { useMutationWrapper } = useRequestProcessor();
    const [children] = useAtom(childList);

    const firstNonPausedIndex = children.findIndex(c => c?.pause_state !== 1 && c?.length?.includes("Half Day"));
    const [selectedChild, setSelectedChild] = useState(firstNonPausedIndex >= 0 ? firstNonPausedIndex : 0);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [error, setError] = useState("");

    const extendedDaycare = useMutationWrapper(["extendedDaycare"], () =>
        submitExtendedDaycare(
            startDate,
            endDate,
            children[selectedChild]
        )
    );

    const validate = () => {
        if (!startDate) {
            setError("Please select a start date");
            return false;
        } else if (!endDate) {
            setError("Please select an end date");
            return false;
        } else {
            const start = moment(startDate).startOf("day");
            const end = moment(endDate).startOf("day");
            const today = moment().startOf("day");

            if (start.isBefore(today) || end.isBefore(today)) {
                setError("Only current and future dates are allowed");
                return false;
            }
            if (end.isBefore(start)) {
                setError("End date cannot be before start date");
                return false;
            }
            const days = end.diff(start, "days");
            // Inclusive window: start date + 29 days = 30 total days
            if (days > 29) {
                setError("Maximum 30 days can be selected");
                return false;
            }
        }
        setError("");
        return true;
    };

    const submitRequest = () => {
        if (validate()) extendedDaycare.mutate();
    };

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
            {extendedDaycare.isLoading && <SpinnerLoading />}

            {/* Header */}
            <div className="sticky top-0 bg-white border-b p-4 flex items-center space-x-3">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 cursor-pointer" onClick={() => navigate(-1)}>Extended Daycare</h2>
            </div>

            <div className="p-4 space-y-4">

                {/* Child Selection */}
                {children.filter(c => c?.pause_state !== 1 && c?.length?.includes("Half Day")).length > 1 && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Child
                        </label>
                        <CustomSelect
                            value={selectedChild}
                            onChange={(val) => setSelectedChild(Number(val))}
                            options={children
                                .map((child, idx) => ({ value: idx, label: `${child?.name} ${child?.last_name}` }))
                                .filter(opt => children[opt.value]?.pause_state !== 1 && children[opt.value]?.length?.includes("Half Day"))
                            }
                            placeholder="Select Child"
                        />
                    </div>
                )}

                {/* Info Box */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                        Extend daycare services for{" "}
                        {children[selectedChild]?.name.split(" ")[0]}. Choose a start and end date below.
                    </p>
                </div>

                {/* Start Date */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date
                    </label>
                    <FootprintDatePicker
                        minimumDate={moment().startOf("day")}
                        onChange={(date) => {
                            setStartDate(date);
                            // If end date is now outside the allowed window, clear it
                            if (endDate) {
                                const start = moment(date).startOf("day");
                                const end = moment(endDate).startOf("day");
                                const maxEnd = moment(date).add(29, "days").startOf("day");
                                if (end.isBefore(start) || end.isAfter(maxEnd)) {
                                    setEndDate(null);
                                }
                            }
                            setError("");
                        }}
                        inputProps={{
                            placeholder: "Select Date",
                        }}
                    />
                </div>

                {/* End Date */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date
                    </label>
                    <FootprintDatePicker
                        disabled={!startDate}
                        minimumDate={startDate ? moment(startDate).startOf("day") : moment().startOf("day")}
                        maximumDate={startDate ? moment(startDate).add(29, "days").startOf("day") : undefined}
                        onChange={(date) => {
                            setEndDate(date);
                            setError("");
                        }}
                        inputProps={{
                            placeholder: "Select Date",
                            disabled: !startDate,
                        }}
                    />
                </div>

                {/* Error */}
                {error && <p className="text-red-500 text-sm">{error}</p>}

                {/* Submit */}
                <button
                    onClick={submitRequest}
                    disabled={extendedDaycare.isLoading}
                    className="w-full bg-cyan-500 text-white py-3 rounded-xl font-semibold hover:bg-cyan-600 transition disabled:opacity-60"
                >
                    Submit Request
                </button>
            </div>
        </div>
    );
};

export default ExtendedDaycare;
